import React from 'react';
import { motion } from 'framer-motion';
import { COMPANY, addressOneLine } from '../data/company';

const PrivacySmartMemorySaver = () => {
  return (
    <div className="pt-24 pb-20 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 legal-content">
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        <span className="text-signal font-semibold tracking-wider text-sm uppercase mb-2 block">App Specific Policy</span>
        <h1>Smart Memory Saver Privacy Policy</h1>
        <p className="text-sm border-l-2 border-signal pl-4 mb-8">
          Last Updated: September 2026 · Applies to version 1.1.0
        </p>

        <h2>The short version</h2>
        <p>
          Smart Memory Saver collects nothing, sends nothing, and has no servers. It makes no network
          requests at all.
        </p>

        <h2>1. Who is responsible</h2>
        <p>
          The publisher is <strong>{COMPANY.legalName}</strong>, a company registered in
          {' '}{COMPANY.jurisdiction} under company number <strong>{COMPANY.number}</strong>,
          registered office {addressOneLine}. {COMPANY.tradingName} is a trading name of that company.
        </p>

        <h2>2. What the extension can see</h2>
        <p>
          To decide whether a tab is safe to put to sleep, the extension reads, in your browser&apos;s
          memory only:
        </p>
        <ul>
          <li>The <strong>URL</strong> of each open tab, reduced to its domain, to match your exclusion list and to recognise video-call sites.</li>
          <li>The <strong>title</strong> of each open tab, to show it in the tab list.</li>
          <li>Whether a tab is <strong>active, pinned, playing audio, or already asleep</strong>.</li>
          <li><strong>When each tab was last used</strong>, to measure how long it has been idle.</li>
        </ul>
        <p>
          This is what the <code>tabs</code> permission grants. Without it the extension cannot tell a
          video call from a forgotten article, and would have to sleep tabs blindly.
        </p>

        <h2>3. What is stored, and where</h2>
        <p>
          Three items are written to <code>chrome.storage.local</code>, which lives on your own
          computer:
        </p>
        <ul>
          <li><code>sms_settings</code> — your mode, idle threshold, protection toggles and exclusion list.</li>
          <li><code>sms_stats</code> — two counters, tabs slept and tabs woken, plus the date you last cleared them.</li>
          <li><code>sms_lastActivity</code> — a map of tab id to timestamp, capped at 500 entries and reset when Chrome restarts.</li>
        </ul>
        <p>
          No page content, no browsing history, no tab URLs and no titles are ever persisted. Nothing
          is written to <code>chrome.storage.sync</code>, so nothing leaves the machine through your
          Chrome profile either. You can clear the counters at any time from the Overview section, and
          removing the extension deletes all of it.
        </p>

        <h2>4. What is never done</h2>
        <ul>
          <li>No data is transmitted anywhere. The manifest sets <code>connect-src &apos;none&apos;</code>, so the browser itself blocks any network connection from the extension&apos;s pages.</li>
          <li>No remote scripts, stylesheets, fonts or images are loaded. Everything the extension needs ships in the package.</li>
          <li>No analytics, telemetry, crash reporting or advertising identifiers.</li>
          <li>Nothing is sold or shared, because nothing is collected.</li>
          <li>No account, sign-in or email address is required or accepted.</li>
        </ul>

        <h2>5. Permissions, and why each one exists</h2>
        <ul>
          <li><code>tabs</code> — read tab state and URLs to decide what is safe to sleep, and to sleep it.</li>
          <li><code>storage</code> — save your settings and counters locally.</li>
          <li><code>alarms</code> — run the periodic idle check without keeping a process alive.</li>
          <li><code>contextMenus</code> — add the right-click entries.</li>
        </ul>
        <p>
          There are no <code>host_permissions</code>: the extension never injects a script into a page
          and never reads page content.
        </p>

        <h2>6. Children</h2>
        <p>
          The extension is not directed at children and collects no personal information from anyone.
        </p>

        <h2>7. Changes</h2>
        <p>
          Any future change to this policy will ship with a version update and a revised date at the
          top of this page.
        </p>

        <h2>8. Contact</h2>
        <p>
          Questions about this policy: <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a>, or
          write to us at the registered office above.
        </p>
      </motion.div>
    </div>
  );
};

export default PrivacySmartMemorySaver;
